import { useMemo } from "react"
import fp from "lodash/fp"

import type { EntityType } from "types/common.types"
import type { CsvDownloadGroup } from "types/csv.types"

import useDevices from "helpers/hooks/useDevices"
import useSelectedTowers from "helpers/hooks/towers/useSelectedTowers"
import type { BuildGroupsContext, GroupsByKey } from "./downloadGroupsHelpers"
import {
  addTelemetryGroup,
  addWeatherGroup,
  applyExtraFields,
  defsNeedDevices,
  getDefsForEntity,
  getLocationGeohash,
} from "./downloadGroupsHelpers"

export interface UseCsvDownloadGroupsReturn {
  groups: CsvDownloadGroup[]
  isLoading: boolean
}

const useCsvDownloadGroups = (
  entity: EntityType,
  entityId: number,
): UseCsvDownloadGroupsReturn => {
  // The tower is fetched first because the CSV def set depends on its tower_health
  // feature flag (see getTowerCsvPlotDefs).
  const towerIds = useMemo(
    () => (entity === "tower" ? [entityId] : []),
    [entity, entityId],
  )
  const { towersById, towersByIdLoaded } = useSelectedTowers({
    towerIds,
    skip: entity !== "tower",
  })
  const tower = towersById[entityId]

  // The ambient defs (which depend on the device) never introduce a non-self
  // resolver, so needsDevices can be derived from the device-independent base.
  const needsDevices = useMemo(
    () => defsNeedDevices(getDefsForEntity(entity, tower, undefined)),
    [entity, tower],
  )
  const {
    devsWRelsById,
    isLoading: devicesLoading,
    isUninitialized: devicesUninitialized,
  } = useDevices({ skip: !needsDevices })

  const towersReady = entity !== "tower" || towersByIdLoaded
  const devicesReady = !needsDevices || (!devicesUninitialized && !devicesLoading)
  const isLoading = !towersReady || !devicesReady

  const groups = useMemo(() => {
    const contextEntity =
      entity === "device" && devsWRelsById ? devsWRelsById[entityId]?.device : undefined

    const context: BuildGroupsContext = {
      entityId,
      contextEntity,
      devsWRelsById,
      towerGeohash: getLocationGeohash(tower?.geo_location),
      deviceGeohash: getLocationGeohash(contextEntity?.settings?.geo_location),
    }

    const defs = getDefsForEntity(entity, tower, contextEntity)
    const baseGroups = defs.reduce<GroupsByKey>((acc, def) => {
      switch (def.dataSource?.service) {
        case "weather":
          return addWeatherGroup(acc, def, context)
        case "telemetry":
          return addTelemetryGroup(acc, def, context)
        default:
          return acc
      }
    }, {})

    return fp.values(applyExtraFields(baseGroups, entity, entityId))
  }, [entity, entityId, devsWRelsById, tower])

  return { groups, isLoading }
}

export default useCsvDownloadGroups
